/**
 * CR337 + CR339 + CR306: Ribbon Color Resolution
 * 
 * Resolves the effective ribbon fill color using a strict priority chain:
 * 1. custom_hex  → use color_hex directly
 * 2. theme_token → use Theme Labs dealTypeThemes (CR339), fallback to legacy default
 * 3. asset_default → use app_assets.default_tint from Asset Studio
 * 4. styling_fallback → use styling.ribbonBaseHex (CR306: preserved legacy behavior)
 * 5. Fallback → deal-type default ribbon color
 * 
 * Returns either a CSS color string (for inline style) or a Tailwind class.
 */

import type { DealCategory } from "@/lib/deals/dealTypeRegistry";
import { normalizeHex } from "@/lib/deals/hexColorUtils";
import type { DealTypeThemeColors } from "@/lib/deals/globalThemeTokens";

// ================================================================
// TYPES
// ================================================================

export type RibbonColorSource = "custom_hex" | "theme_token" | "asset_default";

export interface RibbonColorConfig {
  /** Which color source to use */
  color_source: RibbonColorSource;
  /** HEX color value (used when color_source = "custom_hex") */
  color_hex?: string | null;
}

export interface ResolvedRibbonColor {
  /** CSS color value for inline styles (hex string), or null if using a Tailwind class */
  cssColor: string | null;
  /** Tailwind background class (e.g. "bg-emerald-600"), or null if using inline */
  tailwindClass: string | null;
  /** The source that produced this color */
  source: RibbonColorSource | "styling_fallback" | "fallback";
  /** Human-readable label for debug display */
  debugLabel: string;
}

// ================================================================
// DEAL TYPE → DEFAULT COLOR MAP
// ================================================================

const DEAL_TYPE_DEFAULT_COLORS: Record<DealCategory, string> = {
  BONUS: "bg-emerald-600",
  DISCOUNT: "bg-orange-500",
  POINTS: "bg-purple-600",
  IN_KIND: "bg-amber-600",
  UNCLASSIFIED: "bg-slate-600",
};

const DEAL_TYPE_DEFAULT_HEX: Record<DealCategory, string> = {
  BONUS: "#059669",
  DISCOUNT: "#F97316",
  POINTS: "#9333EA",
  IN_KIND: "#D97706",
  UNCLASSIFIED: "#475569",
};

// ================================================================
// RESOLVER
// ================================================================

/**
 * Resolve the effective ribbon color from the configuration chain.
 * 
 * Priority:
 * 1. custom_hex → inline CSS color from color_hex
 * 2. theme_token → Theme Labs per-deal-type color (CR339), or Tailwind class fallback
 * 3. asset_default → default_tint from app_assets (if provided)
 * 4. styling_fallback → styling.ribbonBaseHex (CR306: preserved legacy behavior)
 * 5. Fallback → deal-type default Tailwind class
 * 
 * @param dealTypeThemes - CR339: Optional Theme Labs per-deal-type theme colors
 * @param stylingFallbackHex - CR306: Optional styling.ribbonBaseHex for legacy fallback
 */
export function resolveRibbonColor(
  config: RibbonColorConfig | undefined | null,
  dealCategory: DealCategory,
  assetDefaultTint?: string | null,
  dealTypeThemes?: Record<string, DealTypeThemeColors> | null,
  stylingFallbackHex?: string | null,
): ResolvedRibbonColor {
  const source = config?.color_source ?? "theme_token";

  // 1. Custom HEX
  if (source === "custom_hex") {
    const hex = config?.color_hex ? normalizeHex(config.color_hex) : null;
    if (hex) {
      return {
        cssColor: hex,
        tailwindClass: null,
        source: "custom_hex",
        debugLabel: `Custom: ${hex}`,
      };
    }
    // Invalid/empty hex → fall through to theme
  }

  // 2. Theme token → CR339: Theme Labs per-deal-type colors first, then legacy Tailwind
  if (source === "theme_token") {
    const themeLabsHex = dealTypeThemes?.[dealCategory]?.ribbonBaseHex;
    if (themeLabsHex) {
      const normalized = normalizeHex(themeLabsHex);
      if (normalized) {
        return {
          cssColor: normalized,
          tailwindClass: null,
          source: "theme_token",
          debugLabel: `Theme Labs: ${normalized}`,
        };
      }
    }
    // Fallback to legacy Tailwind class
    return {
      cssColor: null,
      tailwindClass: DEAL_TYPE_DEFAULT_COLORS[dealCategory] ?? DEAL_TYPE_DEFAULT_COLORS.UNCLASSIFIED,
      source: "theme_token",
      debugLabel: `Theme: ${dealCategory}`,
    };
  }

  // 3. Asset default tint
  if (source === "asset_default") {
    if (assetDefaultTint) {
      const hex = normalizeHex(assetDefaultTint);
      if (hex) {
        return {
          cssColor: hex,
          tailwindClass: null,
          source: "asset_default",
          debugLabel: `Asset tint: ${hex}`,
        };
      }
    }
    // No valid asset tint → fall through to fallback
  }

  // 4. CR306: Styling fallback (styling.ribbonBaseHex from legacy card styling)
  if (stylingFallbackHex) {
    const hex = normalizeHex(stylingFallbackHex);
    if (hex) {
      return {
        cssColor: hex,
        tailwindClass: null,
        source: "styling_fallback",
        debugLabel: `Styling override: ${hex}`,
      };
    }
  }

  // 5. Fallback: deal-type default (check Theme Labs first)
  const themeLabsFallback = dealTypeThemes?.[dealCategory]?.ribbonBaseHex;
  if (themeLabsFallback) {
    const normalized = normalizeHex(themeLabsFallback);
    if (normalized) {
      return {
        cssColor: normalized,
        tailwindClass: null,
        source: "fallback",
        debugLabel: `Theme Labs fallback: ${normalized}`,
      };
    }
  }
  
  return {
    cssColor: null,
    tailwindClass: DEAL_TYPE_DEFAULT_COLORS[dealCategory] ?? DEAL_TYPE_DEFAULT_COLORS.UNCLASSIFIED,
    source: "fallback",
    debugLabel: `Default: ${dealCategory}`,
  };
}

/**
 * Get the HEX color string for a resolved ribbon color.
 * Useful for previews that need an actual color value regardless of source.
 * @param stylingFallbackHex - CR306: Optional styling.ribbonBaseHex for legacy fallback
 */
export function getResolvedRibbonHex(
  config: RibbonColorConfig | undefined | null,
  dealCategory: DealCategory,
  assetDefaultTint?: string | null,
  dealTypeThemes?: Record<string, DealTypeThemeColors> | null,
  stylingFallbackHex?: string | null,
): string {
  const resolved = resolveRibbonColor(config, dealCategory, assetDefaultTint, dealTypeThemes, stylingFallbackHex);
  if (resolved.cssColor) return resolved.cssColor;
  // Check Theme Labs for fallback hex
  const themeHex = dealTypeThemes?.[dealCategory]?.ribbonBaseHex;
  if (themeHex) {
    const normalized = normalizeHex(themeHex);
    if (normalized) return normalized;
  }
  // If Tailwind class, return the known hex for that deal type
  return DEAL_TYPE_DEFAULT_HEX[dealCategory] ?? DEAL_TYPE_DEFAULT_HEX.UNCLASSIFIED;
}

export { DEAL_TYPE_DEFAULT_HEX, DEAL_TYPE_DEFAULT_COLORS };
